import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { X, Star, Flame, LogOut, Shield, Clock, History, Trophy, UserCircle, BookOpen, Gift, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';

interface GeminiMenuProps {
  isOpen: boolean;
  onClose: () => void;
  user: {
    displayName: string;
    team: string;
    xp: number;
    streak: number;
    role?: string;
  } | null;
}

export function GeminiMenu({ isOpen, onClose, user }: GeminiMenuProps) {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const navItems = [
    { label: t("home.preshift"), icon: Clock, path: "/pre-shift-checkin" },
    { label: t("home.history"), icon: History, path: "/history" },
    { label: t("home.quests"), icon: Gift, path: "/quests" },
    { label: t("home.learning"), icon: BookOpen, path: "/learning-modules" },
    { label: t("home.leaderboard"), icon: Trophy, path: "/leaderboard" },
    { label: t("home.editProfile"), icon: UserCircle, path: "/profile" },
    { label: t("home.help"), icon: HelpCircle, path: "/help" },
  ];

  const handleNavigate = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/signup");
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-foreground/20 backdrop-blur-sm z-50"
        onClick={onClose}
      />
      
      {/* Menu Panel */}
      <motion.div
        initial={{ x: "-100%" }}
        animate={{ x: 0 }}
        exit={{ x: "-100%" }}
        transition={{ type: "spring", damping: 25, stiffness: 200 }}
        className="fixed left-0 top-0 bottom-0 w-64 bg-background z-50 shadow-2xl flex flex-col pt-safe"
      >
        {/* Close Button */}
        <div className="flex justify-end p-2">
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8 rounded-full">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Compact User Card */}
        {user && (
          <div className="px-3 pb-3">
            <div className="rounded-lg bg-muted/50 p-3">
              <div className="flex items-center gap-2.5">
                <div className="h-9 w-9 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                  <span className="text-sm font-bold text-primary">
                    {user.displayName.charAt(0)}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate">{user.displayName}</p>
                  <p className="text-xs text-muted-foreground truncate">{user.team}</p>
                </div>
              </div>
              {/* XP and Streak - inline */}
              <div className="flex gap-3 mt-2.5 pt-2.5 border-t border-border/50">
                <div className="flex items-center gap-1">
                  <Star className="h-3.5 w-3.5 text-primary" />
                  <span className="font-semibold text-xs">{user.xp}</span>
                  <span className="text-xs text-muted-foreground">XP</span>
                </div>
                <div className="flex items-center gap-1">
                  <Flame className="h-3.5 w-3.5 text-success" />
                  <span className="font-semibold text-xs">{user.streak}</span>
                  <span className="text-xs text-muted-foreground">hari</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Compact Navigation List */}
        <nav className="flex-1 px-2 overflow-y-auto">
          {/* Supervisor Link */}
          {user?.role === 'supervisor' && (
            <button
              onClick={() => handleNavigate('/supervisor')}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors text-left mb-0.5"
            >
              <Shield className="h-4 w-4 text-primary" />
              <span className="text-sm">{t("home.supervisor")}</span>
            </button>
          )}
          
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => handleNavigate(item.path)}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors text-left"
            >
              <item.icon className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">{item.label}</span>
            </button>
          ))}
        </nav>

        {/* Logout - bottom */}
        <div className="p-3 border-t border-border/50">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-destructive hover:bg-destructive/10 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            <span className="text-sm">{t("home.logout")}</span>
          </button>
        </div>
      </motion.div>
    </>
  );
}
